// Cuisine & Region taxonomy
export const REGIONS = {
  'East Asia': ['Japanese', 'Chinese', 'Korean'],
  'Southeast Asia': ['Thai', 'Vietnamese', 'Filipino', 'Indonesian', 'Malaysian'],
  'South Asia': ['Indian', 'Sri Lankan', 'Pakistani'],
  'Europe': ['Italian', 'French', 'Spanish', 'Greek', 'British', 'German', 'Scandinavian', 'Eastern European'],
  'Americas': ['American', 'Southern US', 'Mexican', 'Brazilian', 'Peruvian', 'Cajun/Creole', 'Canadian'],
  'Caribbean': ['Jamaican', 'Cuban', 'Trinidadian', 'Puerto Rican'],
  'Middle East': ['Lebanese', 'Turkish', 'Persian', 'Israeli'],
  'Africa': ['Moroccan', 'Ethiopian', 'Nigerian', 'South African'],
  'Oceania': ['Australian', 'Hawaiian', 'Polynesian'],
  'Mediterranean': ['Mediterranean', 'North African'],
};

export const ALL_CUISINES = Object.values(REGIONS).flat();

// Age group definitions
export const AGE_GROUPS = {
  baby: { label: 'Baby', description: '6-12 months', icon: '👶' },
  toddler: { label: 'Toddler', description: '1-3 years', icon: '🧒' },
  kid: { label: 'Kid', description: '4-12 years', icon: '👦' },
  teen: { label: 'Teen', description: '13-19 years', icon: '🧑' },
  adult: { label: 'Adult', description: '20+ years', icon: '👨‍🍳' },
};

export const AGE_GROUP_KEYS = Object.keys(AGE_GROUPS);

// Audience taxonomy — who a recipe is written to feed (family-kitchen model).
// Every recipe carries exactly one `audience` value. These four ids are the
// coordinate contract with the frontend — do not rename them.
export const AUDIENCES = {
  adults: { label: 'Adults', description: 'Grown-up cooking', icon: '👨‍🍳' },
  family: { label: 'Family Meals', description: 'Cook once, feed everyone', icon: '👨‍👩‍👧‍👦' },
  toddler: { label: 'Toddler', description: '1-3 years', icon: '🧒' },
  baby: { label: 'Baby', description: '6-12 months, weaning', icon: '👶' },
};

export const AUDIENCE_KEYS = Object.keys(AUDIENCES);

// Family-kitchen categories (coordinate contract with the frontend —
// exact ids and labels, do not rename). The `label` is the category name
// used in recipe.categories arrays and the Dexie categories table.
export const FAMILY_CATEGORIES = [
  { id: 'family', label: 'Family Meals' },
  { id: 'baby-toddler', label: 'Baby & Toddler' },
];

// Difficulty levels
export const DIFFICULTY_LEVELS = {
  easy: { label: 'Easy', color: '#4caf50' },
  medium: { label: 'Medium', color: '#ff9800' },
  hard: { label: 'Hard', color: '#f44336' },
};

// Spice levels
export const SPICE_LEVELS = {
  0: { label: 'No Spice', icon: '' },
  1: { label: 'Mild', icon: '🌶️' },
  2: { label: 'Medium', icon: '🌶️🌶️' },
  3: { label: 'Hot', icon: '🌶️🌶️🌶️' },
  4: { label: 'Very Hot', icon: '🌶️🌶️🌶️🌶️' },
  5: { label: 'Extreme', icon: '🌶️🌶️🌶️🌶️🌶️' },
};

// Enhanced tag categories
export const DIETARY_TAGS = [
  'high-protein',
  'high-iron',
  'high-fiber',
  'low-carb',
  'low-calorie',
  'low-sodium',
  'keto',
  'paleo',
  'vegetarian',
  'vegan',
  'gluten-free',
  'dairy-free',
  'nut-free',
  'egg-free',
  'sugar-free',
  'whole30',
  'pescatarian',
  'halal',
  'kosher',
];

export const COOKING_STYLE_TAGS = [
  'one-pan',
  'one-pot',
  'sheet-pan',
  'slow-cooker',
  'instant-pot',
  'air-fryer',
  'no-cook',
  'grilling',
  'smoking',
  'stir-fry',
  'baking',
  'raw',
  'fermented',
];

export const CONVENIENCE_TAGS = [
  'under-15-min',
  'under-30-min',
  'meal-prep',
  'batch-cooking',
  'freezer-friendly',
  'make-ahead',
  '5-ingredients-or-less',
  'budget-friendly',
  'pantry-staples',
];

export const OCCASION_TAGS = [
  'date-night',
  'family-friendly',
  'party',
  'holiday',
  'game-day',
  'picnic',
  'camping',
  'lunchbox',
  'brunch',
  'comfort-food',
  'weeknight-dinner',
  'sunday-dinner',
  'celebration',
];

export const ALL_TAGS = [
  ...DIETARY_TAGS,
  ...COOKING_STYLE_TAGS,
  ...CONVENIENCE_TAGS,
  ...OCCASION_TAGS,
];

// Default categories (expanded)
export const DEFAULT_CATEGORIES = [
  { name: 'Breakfast', position: 0 },
  { name: 'Lunch', position: 1 },
  { name: 'Dinner', position: 2 },
  { name: 'Dessert', position: 3 },
  { name: 'Snacks', position: 4 },
  { name: 'Soups & Stews', position: 5 },
  { name: 'Appetizers', position: 6 },
  { name: 'Sides', position: 7 },
  { name: 'Salads', position: 8 },
  { name: 'Drinks & Smoothies', position: 9 },
  { name: 'Baking', position: 10 },
  { name: 'Sauces & Dips', position: 11 },
  { name: 'Baby Food', position: 12 },
  { name: 'Family Meals', position: 13 },
  { name: 'Baby & Toddler', position: 14 },
];

// Nutrition reference daily values (for % DV calculations)
export const DAILY_VALUES = {
  calories: 2000,
  protein: 50, // g
  carbs: 275, // g
  fat: 78, // g
  fiber: 28, // g
  sugar: 50, // g
  sodium: 2300, // mg
  iron: 18, // mg
  calcium: 1300, // mg
  vitaminC: 90, // mg
  potassium: 4700, // mg
  vitaminA: 900, // mcg
};

// Category emoji mapping (expanded)
export const CATEGORY_EMOJIS = {
  'Breakfast': '🍳',
  'Lunch': '🥪',
  'Dinner': '🍽️',
  'Dessert': '🍰',
  'Snacks': '🍿',
  'Soups & Stews': '🍲',
  'Appetizers': '🥟',
  'Sides': '🥗',
  'Salads': '🥬',
  'Drinks & Smoothies': '🥤',
  'Baking': '🧁',
  'Sauces & Dips': '🫕',
  'Baby Food': '👶',
  'Family Meals': '👨‍👩‍👧‍👦',
  'Baby & Toddler': '🍼',
};
